import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, UserPlus } from 'lucide-react';
import { fullName, formatDate } from '../../lib/api';
import { listAgendas, listConceptos, createCita, getQuirofanoDia, primerHueco, ofrecerConfirmacionWhatsApp, minutosConcepto, conceptoEsIntervencion, valorTipoCita, leerTipoCita, nombreReserva } from '../../lib/citasApi';
import { Button } from '../ui/button';
import CirujanoSelect from './CirujanoSelect';
import LugarQuirofanoSelect from './LugarQuirofanoSelect';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import FreeSlotPicker from './FreeSlotPicker';
import ConceptoOptions from './ConceptoOptions';
import IntervencionSelect from './IntervencionSelect';
import InfoClinicaPaciente from './InfoClinicaPaciente';
import PatientFormDialog, { fichaDesdeNombre } from '../PatientFormDialog';
import {
  AvisoProhibidoEnCita, DialogoPacienteProhibido, esErrorProhibido, useAvisoProhibido,
} from './AvisoPacienteProhibido';

/**
 * Diálogo "Dar cita" — flujo mínimo en 3 pasos dentro de un mismo modal:
 * agenda → concepto → hueco (FreeSlotPicker). Pensado para que administración
 * dé una cita en segundos desde cualquier pantalla que tenga al paciente.
 *
 * Props:
 *  - open / onOpenChange: control del Dialog
 *  - patient: objeto con al menos {id} y nombre ({name, surname1...} o patient_name)
 *  - onCreated: callback opcional tras crear la cita con éxito (recibe la cita)
 *  - titulo: título del diálogo (default "Dar cita")
 *  - soloQuirofano: si true, solo muestra agendas de quirófano (modo "Agendar
 *    cirugía" desde un presupuesto). El tipo de cirugía lo elige quien agenda
 *    (hay cuatro, cada uno con su duración); solo se preselecciona si el
 *    catálogo tiene un único tipo de cirugía.
 *  - defaultProcedimiento: texto inicial del procedimiento (p. ej. la cirugía
 *    del presupuesto)
 *  - autoPrimerHueco: al elegir agenda, busca y preselecciona el PRIMER hueco
 *    disponible automáticamente (se puede cambiar a mano)
 */

function AgendaCard({ agenda, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`citas-agenda-selector-${agenda.code || agenda.id}`}
      className={`flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
        selected
          ? 'border-[#2E6E5E] bg-[rgba(46,110,94,0.10)] ring-1 ring-[#2E6E5E]'
          : 'border-[#dbe3ee] bg-white hover:border-[#2E6E5E] hover:bg-[rgba(46,110,94,0.05)]'
      }`}
    >
      <span
        className="w-3 h-3 rounded-full shrink-0"
        style={{ backgroundColor: agenda.color || '#2E6E5E' }}
        aria-hidden="true"
      />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-[#13231E] truncate">{agenda.name}</span>
        {agenda.doctor_name && (
          <span className="block text-xs text-muted-foreground truncate">{agenda.doctor_name}</span>
        )}
      </span>
    </button>
  );
}

export default function DarCitaDialog({
  open, onOpenChange, patient, onCreated,
  titulo = 'Dar cita', soloQuirofano = false, defaultProcedimiento = '', autoPrimerHueco = false,
  defaultConceptCode = '', defaultAgendaId = null,
}) {
  const [agendas, setAgendas] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  const [agendaId, setAgendaId] = useState(null);
  const [conceptCode, setConceptCode] = useState('');
  // «Reserva <tipo>» (12/10/2026): el mismo tipo con la marca de reserva.
  const [reserva, setReserva] = useState(false);
  const [slot, setSlot] = useState(null); // {date, start} | null
  // Duración corregida a mano (string) o null = la del tipo de cita en la
  // agenda elegida. Se descarta al cambiar de tipo o de agenda, que la
  // vuelven a rellenar sola (2026-09-03, dirección: cada tipo de cirugía lleva
  // su duración, editable).
  const [duracionManual, setDuracionManual] = useState(null);
  // Detalles de la cita: OBLIGATORIOS y siempre a la vista (2026-09-03,
  // Dirección, punto 5); antes iban colapsados y eran opcionales.
  const [notes, setNotes] = useState('');
  // «Tocado»: el aviso rojo de detalles vacíos solo tras salir del campo o
  // intentar guardar. Nada más abrir salía en rojo antes de escribir nada
  // (revisión 2026-09-03); el botón sigue deshabilitado mientras esté vacío.
  const [notesTocado, setNotesTocado] = useState(false);
  const [saving, setSaving] = useState(false);
  // Agendas de quirófano: lugar de la intervención; externos: cirujano.
  const [lugar, setLugar] = useState('');
  const [cirujano, setCirujano] = useState('');
  // Quirófano: procedimiento ("Blefaro completa + CO2"). El backend detecta
  // los consentimientos por keywords y los crea en la ficha del paciente.
  const [procedimiento, setProcedimiento] = useState('');

  // Contacto creado desde este mismo diálogo (administración, 2026-09-11). Hay
  // citas y presupuestos que llegan con el NOMBRE del paciente pero sin
  // ficha: antes el botón «Confirmar cita» no hacía absolutamente nada (el
  // `confirm` se iba de vacío al no haber id) y no había forma de salir sin
  // irse a la sección Pacientes.
  const [creado, setCreado] = useState(null);
  const [newPatientOpen, setNewPatientOpen] = useState(false);
  const paciente = creado || patient;
  const sinFicha = !paciente?.id;

  // PACIENTE PROHIBIDO (14/10/2026): se sabe por la ficha que llega, por la
  // que lee el recuadro de información clínica o por el 409 del servidor.
  // Aviso emergente sin el motivo; la cita, solo con la excepción de admin o
  // dirección (ver AvisoPacienteProhibido).
  const [fichaLeida, setFichaLeida] = useState(null);
  const [prohibidoServidor, setProhibidoServidor] = useState(false);
  useEffect(() => { setFichaLeida(null); setProhibidoServidor(false); }, [open, paciente?.id]);
  const prohibido = paciente?.prohibido === true || prohibidoServidor
    || (fichaLeida?.id === paciente?.id && fichaLeida?.prohibido === true);
  const aviso = useAvisoProhibido(open && prohibido, open ? paciente?.id : null);

  const patientName = fullName(paciente) || paciente?.name || paciente?.patient_name || '';
  // Memorizado: PatientFormDialog reinicia el formulario cada vez que cambia
  // `initial`, y un objeto nuevo por render borraría lo tecleado.
  const nuevoContactoInicial = useMemo(
    () => (newPatientOpen ? fichaDesdeNombre(patientName) : null),
    [newPatientOpen, patientName],
  );

  // Al abrir: reset + carga de agendas y conceptos.
  useEffect(() => {
    if (!open) return;
    setCreado(null);
    setNewPatientOpen(false);
    setAgendaId(null);
    setConceptCode('');
    setReserva(false);
    setSlot(null);
    setDuracionManual(null);
    setNotes('');
    setNotesTocado(false);
    setSaving(false);
    setLugar('');
    setCirujano('');
    setProcedimiento(defaultProcedimiento || '');
    setLoadingData(true);
    Promise.all([listAgendas(), listConceptos()])
      .then(([ags, cons]) => {
        let list = Array.isArray(ags) ? ags : [];
        // Modo "Agendar cirugía": solo quirófanos.
        if (soloQuirofano) {
          list = list.filter((a) => a.tipo === 'quirofano');
          // Tipo de cirugía: desde 2026-09-03 hay cuatro (párpados), cada
          // uno con su duración, así que no se preselecciona ninguno: lo
          // elige quien agenda. Si el catálogo solo tiene uno, ese.
          const cirugias = (Array.isArray(cons) ? cons : []).filter((c) => c.category === 'cirugia');
          if (cirugias.length === 1) setConceptCode(cirugias[0].code);
        } else if (defaultConceptCode && (Array.isArray(cons) ? cons : []).some((c) => c.code === defaultConceptCode)) {
          // Concepto preseleccionado (p. ej. "primera-revision" desde quirófano).
          setConceptCode(defaultConceptCode);
        }
        setAgendas(list);
        setConceptos(Array.isArray(cons) ? cons : []);
        // Si venimos del contexto de una cita/parte, su agenda YA se sabe:
        // precargarla en vez de volver a pedir todo el proceso (2026-08-19).
        // El backend ya ordena por rol; si solo hay una, también se preselecciona.
        if (defaultAgendaId && list.some((a) => a.id === defaultAgendaId)) {
          setAgendaId(defaultAgendaId);
        } else if (list.length === 1) setAgendaId(list[0].id);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || 'No se pudieron cargar las agendas');
      })
      .finally(() => setLoadingData(false));
  }, [open, soloQuirofano, defaultProcedimiento, defaultConceptCode, defaultAgendaId]);

  const selectedConcept = useMemo(
    () => conceptos.find((c) => c.code === conceptCode) || null,
    [conceptos, conceptCode],
  );

  const selectedAgenda = useMemo(
    () => agendas.find((a) => a.id === agendaId) || null,
    [agendas, agendaId],
  );

  // Duración de la cita: la del tipo elegido en esta agenda (override o por
  // defecto), salvo que se haya corregido a mano. Vacía o no válida → el
  // backend resuelve la del concepto, como hasta ahora.
  const minutosDelConcepto = minutosConcepto(selectedAgenda, selectedConcept);
  const duration = duracionManual ?? (minutosDelConcepto ? String(minutosDelConcepto) : '');
  const durNum = parseInt(duration, 10);
  const durationMinutes = Number.isFinite(durNum) && durNum > 0 ? durNum : undefined;

  // Primer hueco automático: al tener agenda + concepto, buscar y
  // preseleccionar el primer hueco disponible (se puede cambiar a mano).
  useEffect(() => {
    if (!open || !autoPrimerHueco || !agendaId || !conceptCode || slot) return undefined;
    let cancelled = false;
    // Con la paciente: nada de huecos que la dejen en dos sitios (23/9/2026).
    primerHueco({ agendaId, conceptCode, durationMinutes, patientId: paciente?.id })
      .then((r) => {
        if (!cancelled && r?.found) setSlot({ date: r.date, start: r.start });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, autoPrimerHueco, agendaId, conceptCode, slot, durationMinutes, paciente?.id]);

  const isQuirofano = selectedAgenda?.tipo === 'quirofano';
  const isExterna = !!selectedAgenda?.is_externa;
  // La INTERVENCIÓN se pide en quirófano solo si el tipo elegido es una
  // (2/10/2026: una revisión o una consulta previa citadas en quirófano van
  // como en la agenda de consultas, sin intervención ni consentimientos). Un
  // tipo que «lee la nota» (un tratamiento) la deja elegir sin exigirla: si
  // se elige, es una intervención. Sin tipo elegido aún, como siempre.
  const intervencionObligatoria = isQuirofano
    && (!conceptCode || conceptoEsIntervencion(conceptCode, conceptos));
  const pideIntervencion = intervencionObligatoria || (isQuirofano && !!selectedConcept?.lee_la_nota);

  // Config del DÍA de quirófano para la fecha del hueco elegido: si ya está
  // fijada se hereda; si no, la primera cita la fija (pedimos lugar/cirujano).
  const [diaConfig, setDiaConfig] = useState(null);
  useEffect(() => {
    if (!isQuirofano || !agendaId || !slot?.date) {
      setDiaConfig(null);
      return;
    }
    let cancelled = false;
    getQuirofanoDia(agendaId, slot.date)
      .then((cfg) => { if (!cancelled) setDiaConfig(cfg && cfg.lugar ? cfg : null); })
      .catch(() => { if (!cancelled) setDiaConfig(null); });
    return () => { cancelled = true; };
  }, [isQuirofano, agendaId, slot?.date]);

  const needsDiaConfig = isQuirofano && !!slot && !diaConfig;
  // Externos: el cirujano es OBLIGATORIO y solo de la lista de altas
  // (dirección, 2026-09-08, punto 5). Se pide si el día no está fijado o si
  // está fijado sin un cirujano dado de alta (histórico con la clínica):
  // el backend lo marca con `cirujano_registrado: false` y con la cita se
  // corrige el día entero.
  const needsCirujano = isExterna && !!slot && (needsDiaConfig || diaConfig?.cirujano_registrado === false);

  const pickAgenda = (id) => {
    if (id === agendaId) return;
    setAgendaId(id);
    setSlot(null); // los huecos de la otra agenda ya no valen
    setDuracionManual(null); // y su duración puede tener otro override
    setLugar('');
    setCirujano('');
    setProcedimiento(defaultProcedimiento || '');
  };

  const pickConcept = (valor) => {
    const { code, reserva: conReserva } = leerTipoCita(valor);
    setReserva(conReserva);
    // «Reserva X» y «X» son el mismo tipo: ni la duración ni el hueco cambian.
    if (code === conceptCode) return;
    setConceptCode(code);
    setDuracionManual(null); // se rellena sola con la del nuevo tipo
    setSlot(null); // la duración cambia → el hueco elegido deja de valer
  };

  // QUIRÓFANO: con la intervención elegida (a mano o la del presupuesto al
  // «Agendar cirugía»), los detalles se rellenan solos con «Cirugía: X» —
  // el mismo texto que pone el servidor y el Agente IA — mientras quien
  // agenda no haya escrito nada (revisión 2026-09-03: al venir del
  // presupuesto, el botón quedaba bloqueado por unos detalles que ya se
  // sabían). Lo escrito a mano nunca se pisa; si cambia la intervención o
  // la agenda deja de ser de quirófano, el texto automático se actualiza o
  // se retira, y solo él.
  const detallesAuto = pideIntervencion && procedimiento.trim() ? `Cirugía: ${procedimiento.trim()}` : '';
  const ultimoAuto = useRef('');
  useEffect(() => {
    setNotes((actual) => (!actual.trim() || actual === ultimoAuto.current ? detallesAuto : actual));
    ultimoAuto.current = detallesAuto;
  }, [detallesAuto]);

  const faltanDetalles = !notes.trim();
  const errorDetalles = faltanDetalles && notesTocado;

  const confirm = async () => {
    if (!paciente?.id || !agendaId || !conceptCode || !slot) return;
    if (aviso.bloquea) {
      aviso.setAvisoAbierto(true);
      return;
    }
    if (faltanDetalles) {
      setNotesTocado(true);
      toast.error('Los detalles de la cita son obligatorios');
      return;
    }
    if (intervencionObligatoria && !procedimiento.trim()) {
      toast.error('Elige la intervención (obligatoria en quirófano)');
      return;
    }
    if (needsDiaConfig && !lugar) {
      toast.error('Indica dónde es el quirófano de ese día');
      return;
    }
    if (needsCirujano && !cirujano.trim()) {
      toast.error('Elige el cirujano de la lista');
      return;
    }
    setSaving(true);
    try {
      const created = await createCita({
        agenda_id: agendaId,
        patient_id: paciente.id,
        concept_code: conceptCode,
        date: slot.date,
        start: slot.start,
        duration_minutes: durationMinutes,
        notes: notes.trim(),
        // Solo la PRIMERA cita del día fija el quirófano; después se hereda.
        lugar: needsDiaConfig ? lugar : undefined,
        cirujano: needsCirujano ? cirujano.trim() : undefined,
        procedimiento: pideIntervencion ? procedimiento.trim() || undefined : undefined,
        ...(reserva ? { reserva: true } : {}),
        ...(aviso.excepcion ? { excepcion_prohibido: true } : {}),
      });
      const servicio = selectedConcept?.name || conceptCode;
      toast.success(`Cita creada: ${reserva ? nombreReserva(servicio) : servicio} el ${formatDate(slot.date)} a las ${slot.start}`);
      if (created?.consents_created?.length) {
        toast.success(`Consentimientos creados: ${created.consents_created.join(', ')}`);
      }
      // Los Detalles nombran otra cirugía que el procedimiento (23/9/2026):
      // el servidor lo dice en `aviso_documentos` y aquí se enseña tal cual.
      if (created?.aviso_documentos) toast.warning(created.aviso_documentos, { duration: 12000 });
      // Mensaje de CONFIRMACIÓN inmediato (distinto del recordatorio del día
      // anterior): se ofrece nada más agendar.
      ofrecerConfirmacionWhatsApp(created?.id);
      onCreated?.(created);
      onOpenChange(false);
    } catch (e) {
      if (esErrorProhibido(e)) {
        // El servidor sabe que lo está aunque la pantalla no lo supiera.
        setProhibidoServidor(true);
        toast.error(e?.response?.data?.detail);
      } else if (e?.response?.status === 409) {
        toast.error(e?.response?.data?.detail || 'Ese hueco ya está ocupado');
      } else {
        toast.error(e?.response?.data?.detail || 'No se pudo crear la cita');
      }
    } finally {
      setSaving(false);
    }
  };

  const canConfirm =
    !!(paciente?.id && agendaId && conceptCode && slot)
    && !faltanDetalles
    && (!intervencionObligatoria || !!procedimiento.trim())
    && (!needsDiaConfig || !!lugar)
    && (!needsCirujano || !!cirujano.trim())
    && !aviso.bloquea
    && !saving;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="dar-cita-dialog">
          <DialogHeader>
            <DialogTitle>{titulo}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-md border border-[#D9E3DD] bg-[rgba(46,110,94,0.06)] px-3 py-2">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Paciente</div>
              <div className="text-sm font-medium text-[#13231E]" data-testid="dar-cita-patient-name">
                {patientName || '—'}
              </div>
            </div>

            {prohibido && (
              <AvisoProhibidoEnCita
                puedeExcepcion={aviso.puedeExcepcion}
                excepcion={aviso.excepcion}
                onExcepcion={aviso.setExcepcion}
                testId="dar-cita-prohibido"
              />
            )}

            {/* Sin ficha no hay cita que valga: se da de alta aquí mismo, con el
                nombre ya repartido en nombre y apellidos, y queda elegido. */}
            {sinFicha && (
              <div
                className="flex items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2"
                data-testid="dar-cita-sin-ficha"
              >
                <div className="text-xs text-amber-900">
                  Este paciente todavía no tiene ficha. Créala para poder darle la cita.
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0 border-amber-400 text-amber-900 hover:bg-amber-100"
                  onClick={() => setNewPatientOpen(true)}
                  data-testid="dar-cita-nuevo-paciente"
                >
                  <UserPlus className="w-4 h-4 mr-1.5" /> Crear contacto
                </Button>
              </div>
            )}

            {loadingData ? (
              <div className="space-y-3">
                <Skeleton className="h-16 w-full rounded-lg" />
                <Skeleton className="h-10 w-full rounded-md" />
              </div>
            ) : (
              <>
                <div>
                  <Label>Agenda</Label>
                  <div
                    className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1.5"
                    data-testid="citas-agenda-selector"
                  >
                    {agendas.length === 0 ? (
                      <div className="col-span-full text-sm text-muted-foreground">No hay agendas disponibles.</div>
                    ) : (
                      agendas.map((a) => (
                        <AgendaCard key={a.id} agenda={a} selected={agendaId === a.id} onClick={() => pickAgenda(a.id)} />
                      ))
                    )}
                  </div>
                </div>

                <div>
                  <Label>Tipo de cita</Label>
                  <Select value={valorTipoCita(conceptCode, reserva)} onValueChange={pickConcept}>
                    <SelectTrigger className="mt-1.5" data-testid="dar-cita-concepto">
                      <SelectValue placeholder="Elegir tipo de cita…" />
                    </SelectTrigger>
                    <SelectContent>
                      <ConceptoOptions conceptos={conceptos} minutesFor={(c) => minutosConcepto(selectedAgenda, c)} />
                    </SelectContent>
                  </Select>
                </div>

                {/* Duración: se rellena sola al elegir el tipo y se puede corregir */}
                <div>
                  <Label htmlFor="dar-cita-dur">Duración (min)</Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Input
                      id="dar-cita-dur"
                      type="number"
                      min={5}
                      step={5}
                      value={duration}
                      onChange={(e) => setDuracionManual(e.target.value)}
                      className="w-24"
                      data-testid="dar-cita-duration"
                    />
                    <span className="text-xs text-muted-foreground">
                      se rellena sola con el tipo de cita; puedes cambiarla
                    </span>
                  </div>
                </div>

                {/* Quirófano con día ya configurado: se hereda (informativo) */}
                {isQuirofano && slot && diaConfig && (
                  <div
                    className="rounded-md border border-[#D9E3DD] bg-[rgba(46,110,94,0.06)] px-3 py-2 text-sm text-[#13231E]"
                    data-testid="dar-cita-dia-config"
                  >
                    Quirófano del {formatDate(slot.date)} en{' '}
                    <span className="font-semibold">{diaConfig.lugar}</span>
                    {needsCirujano ? (
                      <span className="text-amber-700 font-medium" data-testid="dar-cita-dia-sin-cirujano">
                        {' · Sin cirujano dado de alta'}
                        {diaConfig.cirujano ? ` («${diaConfig.cirujano}» no está en la lista)` : ''}
                        {': elígelo abajo'}
                      </span>
                    ) : diaConfig.cirujano ? (
                      <> · Cirujano: <span className="font-semibold">{diaConfig.cirujano}</span></>
                    ) : null}
                  </div>
                )}

                {/* Primera cita del día: fija el lugar de TODO el quirófano */}
                {needsDiaConfig && (
                  <div>
                    <Label>Lugar del quirófano (todo el día)</Label>
                    <LugarQuirofanoSelect value={lugar} onChange={setLugar} dataTestid="dar-cita-lugar"
                      placeholder="¿Dónde es el quirófano ese día?" />
                  </div>
                )}

                {/* Externos sin cirujano fijado ese día: obligatorio, de la lista */}
                {needsCirujano && (
                  <div>
                    <Label>Cirujano de ese quirófano <span className="text-red-600">*</span></Label>
                    <CirujanoSelect value={cirujano} onChange={setCirujano} dataTestid="dar-cita-cirujano" />
                  </div>
                )}

                {/* Quirófano: intervención OBLIGATORIA desde el catálogo (multi),
                    si el tipo es una intervención (2/10/2026). */}
                {pideIntervencion && (
                  <div>
                    <Label>Intervención {intervencionObligatoria && <span className="text-red-600">*</span>}</Label>
                    <IntervencionSelect
                      value={procedimiento}
                      onChange={setProcedimiento}
                      testId="dar-cita-procedimiento"
                    />
                    <div className="text-xs text-muted-foreground mt-1">
                      Elige una o varias (se combinan). Al guardar se crean solos los consentimientos del tipo de cirugía (+ anestesia).
                    </div>
                  </div>
                )}

                <div>
                  <Label>Día y hora</Label>
                  <div className="mt-1.5">
                    <FreeSlotPicker
                      agendaId={agendaId}
                      conceptCode={conceptCode || null}
                      durationMinutes={durationMinutes}
                      patientId={paciente?.id}
                      value={slot}
                      onChange={setSlot}
                    />
                  </div>
                </div>


                {/* Lista de espera retirada de la interfaz (2026-08-29). */}

                {/* Detalles: obligatorios y siempre visibles (punto 5) */}
                <div>
                  <Label htmlFor="dar-cita-notes">Detalles <span className="text-red-600">*</span></Label>
                  <Textarea
                    id="dar-cita-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    onBlur={() => setNotesTocado(true)}
                    rows={3}
                    placeholder="Ej.: Viene derivada del Dr. López, traer informes previos…"
                    className={`mt-1.5 ${errorDetalles ? 'border-red-300' : ''}`}
                    aria-invalid={errorDetalles}
                    data-testid="dar-cita-notes"
                  />
                  {errorDetalles ? (
                    <div className="text-xs text-red-600 mt-1" data-testid="dar-cita-notes-error">
                      Los detalles de la cita son obligatorios
                    </div>
                  ) : faltanDetalles && (
                    <div className="text-xs text-muted-foreground mt-1" data-testid="dar-cita-notes-ayuda">
                      Obligatorio
                    </div>
                  )}
                </div>

                {/* Alergias, medicación, ácido hialurónico previo y antecedentes
                    médicos de la ficha (solo lectura; punto 9, 2026-09-08) */}
                <InfoClinicaPaciente patientId={paciente?.id} testId="dar-cita-info-clinica" onFicha={setFichaLeida} />
              </>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="dar-cita-cancel">
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={confirm}
              disabled={!canConfirm}
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
              data-testid="dar-cita-confirm"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {slot ? `Confirmar cita — ${formatDate(slot.date)} ${slot.start}` : 'Confirmar cita'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DialogoPacienteProhibido
        open={aviso.avisoAbierto}
        onOpenChange={aviso.setAvisoAbierto}
        puedeExcepcion={aviso.puedeExcepcion}
        onExcepcion={() => aviso.setExcepcion(true)}
        testId="dar-cita-aviso-prohibido"
      />

      {/* Es el formulario de Pacientes, no una copia: los cuatro datos
          obligatorios del alta se piden aquí igual que en su sección. */}
      <PatientFormDialog
        open={newPatientOpen}
        onOpenChange={setNewPatientOpen}
        initial={nuevoContactoInicial}
        onSaved={(p) => { if (p?.id) setCreado(p); }}
      />
    </>
  );
}
